import type { Car, ServiceHistory, ShftService, User } from "@/types/database";
import type { ChartDatum } from "@/lib/analytics";

export type UserListEntry = User & {
  carCount: number;
  serviceCount: number;
};

export type UserPlatformCharts = {
  signupTrend: ChartDatum[];
  topUsersByCars: ChartDatum[];
  servicesByType: ChartDatum[];
};

export type UserPlatformOverview = {
  totalUsers: number;
  adminCount: number;
  totalCarsLinked: number;
  totalServices: number;
  avgCarsPerUser: number;
  latestSignup: string | null;
};

function carsForUser(cars: Car[], user: User): Car[] {
  if (!user.userID) return [];
  const key = user.userID.toUpperCase();
  return cars.filter((car) => car.userID?.toUpperCase() === key);
}

function servicesForCars(
  carIds: Set<string>,
  serviceHistory: ServiceHistory[],
  shftServices: ShftService[]
): number {
  const historyCount = serviceHistory.filter((entry) => carIds.has(entry.carID.toUpperCase())).length;
  const shftCount = shftServices.filter((entry) => carIds.has(entry.carID.toUpperCase())).length;
  return historyCount + shftCount;
}

export function enrichUsers(
  users: User[],
  cars: Car[],
  serviceHistory: ServiceHistory[],
  shftServices: ShftService[]
): UserListEntry[] {
  return users.map((user) => {
    const ownedCars = carsForUser(cars, user);
    const carIds = new Set(ownedCars.map((car) => car.carID.toUpperCase()));
    return {
      ...user,
      carCount: ownedCars.length,
      serviceCount: servicesForCars(carIds, serviceHistory, shftServices),
    };
  });
}

export function getUserPlatformOverview(
  users: User[],
  cars: Car[],
  serviceHistory: ServiceHistory[],
  shftServices: ShftService[]
): UserPlatformOverview {
  const enriched = enrichUsers(users, cars, serviceHistory, shftServices);
  const totalCarsLinked = enriched.reduce((sum, user) => sum + user.carCount, 0);
  const totalServices = enriched.reduce((sum, user) => sum + user.serviceCount, 0);

  return {
    totalUsers: users.length,
    adminCount: users.filter((user) => user.is_admin).length,
    totalCarsLinked,
    totalServices,
    avgCarsPerUser: users.length > 0 ? totalCarsLinked / users.length : 0,
    latestSignup: users[0]?.created_at ?? null,
  };
}

export function getUserSignupChart(users: User[]): ChartDatum[] {
  const counts = new Map<string, { name: string; value: number; sortKey: string }>();

  for (const user of users) {
    const parsed = new Date(user.created_at);
    if (Number.isNaN(parsed.getTime())) continue;
    const sortKey = `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, "0")}`;
    const name = parsed.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
    const existing = counts.get(sortKey);
    if (existing) {
      existing.value += 1;
    } else {
      counts.set(sortKey, { name, value: 1, sortKey });
    }
  }

  return [...counts.values()]
    .sort((a, b) => a.sortKey.localeCompare(b.sortKey))
    .slice(-8)
    .map(({ name, value }) => ({ name, value }));
}

export function getTopUsersByCars(users: UserListEntry[], limit = 6): ChartDatum[] {
  return [...users]
    .sort((a, b) => b.carCount - a.carCount)
    .slice(0, limit)
    .map((user) => ({
      name: [user.firstName, user.lastName].filter(Boolean).join(" ") || `User ${user.id}`,
      value: user.carCount,
    }))
    .filter((entry) => entry.value > 0);
}

export function getServicesByTypeChart(
  serviceHistory: ServiceHistory[],
  shftServices: ShftService[]
): ChartDatum[] {
  const counts = new Map<string, number>();

  for (const entry of serviceHistory) {
    for (const type of entry.serviceType ?? []) {
      counts.set(type, (counts.get(type) ?? 0) + 1);
    }
  }

  for (const entry of shftServices) {
    const type = entry.serviceType ?? entry.serviceName ?? "Shift service";
    counts.set(type, (counts.get(type) ?? 0) + 1);
  }

  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([name, value]) => ({ name, value }));
}

export function getUserPlatformCharts(
  users: User[],
  cars: Car[],
  serviceHistory: ServiceHistory[],
  shftServices: ShftService[]
): UserPlatformCharts {
  const enriched = enrichUsers(users, cars, serviceHistory, shftServices);
  return {
    signupTrend: getUserSignupChart(users),
    topUsersByCars: getTopUsersByCars(enriched),
    servicesByType: getServicesByTypeChart(serviceHistory, shftServices),
  };
}

export function getServiceHistoryForCar(
  carId: string,
  serviceHistory: ServiceHistory[]
): ServiceHistory[] {
  const key = carId.toUpperCase();
  return serviceHistory.filter((entry) => entry.carID.toUpperCase() === key);
}

export function getShftServicesForCar(carId: string, shftServices: ShftService[]): ShftService[] {
  const key = carId.toUpperCase();
  return shftServices.filter((entry) => entry.carID.toUpperCase() === key);
}
